import {
  EARLIEST_NEW_PROGRAM_WEEK,
  HOLIDAY_WEEK,
  LATEST_NEW_PROGRAM_WEEK,
} from '@/constants/program-dates'
import type {
  DeliveryCycle,
  DeliveryZone as PopsDeliveryZone,
  ExceptionDelivery as PopsDeliveryZoneException,
} from '@/types/pops'
import dayjs, { type Dayjs } from '@/utilities/dayjs-config'

import { Meal } from './meal'

export type Delivery = {
  day: string
  mealCodes: string[]
}

type MealsForMealDay = {
  day: string
  meals: string[]
}

export class DeliveryZone {
  id: number
  name: string
  defaultDeliveryWindow: string[]
  defaultDeliveryWindowId: number
  daysOfFoodFirstDeliveryDays: { [key: string]: string[] }
  deliveryCycles: DeliveryCycle[]
  mealAllocations: {
    day: string
    breakfast: boolean
    lunch: boolean
    dinner: boolean
    isDeliveryDay: boolean
    daysOfFoodDelivered: number
  }[]
  minimumMealCount: number
  deliveryWindows: string[][]
  kitchen: string
  kitchenSuffix: string
  programDays: number[]
  startDay: string
  notice: {
    bgColour: string
    title: string
    text: string
    textColour: string
    ctaUrl: string
    ctaText: string
  }
  deliveries: {
    [key: string]: string[]
  }[]
  totalDeliverableMeals: number
  exceptions: DeliveryZoneException[]

  constructor(rawDeliveryZone: PopsDeliveryZone) {
    this.id = rawDeliveryZone.id
    this.name = rawDeliveryZone.name
    this.defaultDeliveryWindow = rawDeliveryZone.default_delivery_window
    this.defaultDeliveryWindowId = rawDeliveryZone.default_delivery_window_id
    this.daysOfFoodFirstDeliveryDays =
      rawDeliveryZone.days_of_food_first_delivery_days
    this.deliveryCycles = rawDeliveryZone.delivery_cycles
    this.mealAllocations = rawDeliveryZone.meal_allocations.map(
      (meal_allocation) => ({
        ...meal_allocation,
        isDeliveryDay: meal_allocation.is_delivery_day,
        daysOfFoodDelivered: meal_allocation.days_of_food_delivered,
      }),
    )
    this.minimumMealCount = rawDeliveryZone.minimum_meal_count
    this.deliveryWindows = rawDeliveryZone.delivery_windows
    this.kitchen = rawDeliveryZone.kitchen
    this.kitchenSuffix = rawDeliveryZone.kitchen_suffix
    this.programDays = rawDeliveryZone.program_days
    this.startDay = rawDeliveryZone.start_day
    this.notice = {
      bgColour: rawDeliveryZone.notice?.bg_colour,
      title: rawDeliveryZone.notice?.title,
      text: rawDeliveryZone.notice?.text,
      textColour: rawDeliveryZone.notice?.text_colour,
      ctaUrl: rawDeliveryZone.notice?.cta_url,
      ctaText: rawDeliveryZone.notice?.cta_text,
    }
    this.deliveries = rawDeliveryZone.deliveries
    this.totalDeliverableMeals =
      (this.programDays && Math.max(...this.programDays)) || 0

    // Create exceptions and filter out any that don't have configs
    this.exceptions = rawDeliveryZone?.exceptions?.map(
      (exception) =>
        new DeliveryZoneException({
          id: rawDeliveryZone.id,
          name: rawDeliveryZone.name,
          exception_name: exception.name,
          default_delivery_window: rawDeliveryZone.default_delivery_window,
          default_delivery_window_id:
            rawDeliveryZone.default_delivery_window_id,
          days_of_food_first_delivery_days:
            exception.days_of_food_first_delivery_days,
          deliveries: exception.deliveries,
          delivery_cycles: exception.delivery_cycles,
          meal_allocations: exception.meal_allocations,
          delivery_windows: rawDeliveryZone.delivery_windows,
          kitchen: rawDeliveryZone.kitchen,
          kitchen_suffix: rawDeliveryZone.kitchen_suffix,
          // TODO: double check this
          program_days: this.programDays.filter(
            (pd) =>
              pd >= (exception.minimum_deliverable_meals || 0) &&
              pd <= (exception.total_deliverable_meals || 0),
          ),
          start_day: exception.start_day,
          total_deliverable_meals: exception.total_deliverable_meals,
          week: exception.week,
        }),
    )
  }

  getStartDates({
    daysOfFood,
    meals = ['breakfast', 'lunch', 'dinner'],
    earliestDate = EARLIEST_NEW_PROGRAM_WEEK,
    latestDate = LATEST_NEW_PROGRAM_WEEK,
    useExceptions = true,
  }: {
    daysOfFood: number
    meals: string[]
    earliestDate?: Dayjs
    latestDate?: Dayjs
    useExceptions?: boolean
  }) {
    const availableDates = []
    let endDate = dayjs(dayjs.max(LATEST_NEW_PROGRAM_WEEK, latestDate))
    let newDate = dayjs(dayjs.max(EARLIEST_NEW_PROGRAM_WEEK, earliestDate))

    while (newDate.isSameOrBefore(endDate, 'day')) {
      const weekDeliveryZone = useExceptions
        ? this.getDeliveryZoneForWeek(newDate)
        : this
      const daysOfFoodForWeek = Math.min(
        daysOfFood,
        weekDeliveryZone.totalDeliverableMeals,
      )
      const deliveries = weekDeliveryZone.getDeliveriesForDaysOfFoodAndMeals(
        daysOfFoodForWeek,
        meals,
      )
      const mealCodes = deliveries.map((delivery) => delivery.mealCodes).flat()
      const firstDate = Meal.getDateFromMealCode(mealCodes[0])

      const isHolidayDayWeek = newDate.isSame(HOLIDAY_WEEK, 'week')
      const isDateAvailable = !(
        isHolidayDayWeek &&
        daysOfFood === 2 &&
        weekDeliveryZone instanceof DeliveryZoneException
      )

      if (isDateAvailable) {
        availableDates.push(dayjs(newDate).isoWeekday(firstDate.isoWeekday()))
      }

      newDate = newDate.add(1, 'week')
    }

    return availableDates
  }

  hasDeliveryZoneExceptionForWeek(week: Dayjs) {
    return (
      this.exceptions &&
      this.exceptions.some((deliveryZone) =>
        deliveryZone.week.isSame(week, 'week'),
      )
    )
  }

  getDeliveryZoneException(week: Dayjs) {
    return (
      this.exceptions &&
      this.exceptions.find((deliveryZone) =>
        deliveryZone.week.isSame(week, 'week'),
      )
    )
  }

  /**
   * Checks if the delivery schedule is changed for the given days of food and week.
   *
   * @param {number} daysOfFood
   * @param {dayjs} week
   * @returns boolean
   */
  isScheduleChanged(daysOfFood: number, week: Dayjs) {
    if (this.isDaysOfFoodChangedForWeek(daysOfFood, week)) return true

    const deliveryZoneException = this.getDeliveryZoneException(week)

    if (!deliveryZoneException) return false

    const deliveries = this.getDeliveriesForDaysOfFood(daysOfFood)
    const exceptionDeliveries =
      deliveryZoneException.getDeliveriesForDaysOfFood(daysOfFood)

    return JSON.stringify(deliveries) !== JSON.stringify(exceptionDeliveries)
  }

  /**
   * Checks if the days of food delivered is changed for the given week.
   *
   * @param {number} daysOfFood
   * @param {dayjs} week
   * @returns boolean
   */
  isDaysOfFoodChangedForWeek(daysOfFood: number, week: Dayjs) {
    const deliveryZoneException = this.getDeliveryZoneException(week)

    if (!deliveryZoneException) return false

    const { totalDeliverableMeals } = deliveryZoneException

    return daysOfFood > totalDeliverableMeals
  }

  /**
   * Uses the delivery zone's deliveries to create a new array with
   * the filtered down meal codes corresponding to the days of food given.
   *
   * @param {number} daysOfFood Days of food in a week
   * @returns {array}
   * @example
   * For a Nationwide delivery zone:
   *
   * getDeliveriesForDaysOfFood(4)
   *
   * Will return:
   * [
   * 	{
   * 		"day": "Tuesday",
   * 		"mealCodes" [
   * 			"D2", "B3", "L3", "D3", "B4", "L4", "D4", "B5", "L5"
   * 		]
   * 	},
   * 	{
   * 		"day": "Friday",
   * 		"mealCodes" [
   * 			"D5", "B6", "L6"
   * 		]
   * 	}
   * ]
   */
  getDeliveriesForDaysOfFood(daysOfFood: number): Delivery[] {
    if (
      this instanceof DeliveryZoneException &&
      this.week.isSame(HOLIDAY_WEEK, 'week') &&
      daysOfFood === 2
    ) {
      daysOfFood = 3
    }

    const firstDeliveryDay =
      this.daysOfFoodFirstDeliveryDays[daysOfFood === 4 ? 5 : daysOfFood][0] // TODO: remove the ternary after 4-day subs are fully sunset
    const firstCycleIndex = this.deliveryCycles.findIndex(
      (dc) => dc.day === firstDeliveryDay,
    )
    const newDeliveryCycles: Delivery[] = []
    let daysOfFoodRemaining = daysOfFood

    for (let i = firstCycleIndex; i < this.deliveryCycles.length; i++) {
      const { day, deliveries } = this.deliveryCycles[i]
      const cycleDaysOfFood = Math.min(
        daysOfFoodRemaining,
        deliveries.length / 3,
      )

      if (daysOfFoodRemaining > 0) {
        const newDeliveryCycle: Delivery = {
          day,
          mealCodes: deliveries.slice(0, cycleDaysOfFood * 3),
        }

        newDeliveryCycles.push(newDeliveryCycle)
      }

      daysOfFoodRemaining -= cycleDaysOfFood
    }

    return newDeliveryCycles
  }

  /**
   * Uses the delivery zone's deliveries to create a new array with
   * the filtered down meals codes corresponding to the days of food
   * and meals given.
   *
   * @param {number} daysOfFood Days of food in a week
   * @param {array} meals Meals included in plan
   * @returns {array}
   * @example
   * For a Nationwide delivery zone:
   *
   * getDeliveriesForDaysOfFoodAndMeals(4, ['breakfast', 'lunch'])
   *
   * Will return:
   * [
   * 	{
   * 		"day": "Tuesday",
   * 		"mealCodes" [
   * 			"B3", "L3", "B4", "L4", "B5", "L5"
   * 		]
   * 	},
   * 	{
   * 		"day": "Friday",
   * 		"mealCodes" [
   * 			"B6", "L6"
   * 		]
   * 	}
   * ]
   */
  getDeliveriesForDaysOfFoodAndMeals(
    daysOfFood: number,
    meals: string[],
  ): Delivery[] {
    const deliveries = this.getDeliveriesForDaysOfFood(daysOfFood)

    return deliveries.map(({ day, mealCodes }) => {
      return {
        day,
        mealCodes: mealCodes.filter((mealCode) => {
          const mealType = Meal.getMealTypeFromMealCode(mealCode)
          const hasMeal = meals.includes(mealType)

          return hasMeal
        }),
      }
    })
  }

  /**
   * Returns an array of objects that represent meal days and
   * the meals within them.
   *
   * @param {number} daysOfFood Days of food in a week
   * @param {array} meals Meals included in plan
   * @returns {array}
   * @example
   * For a Nationwide delivery zone:
   *
   * getMealDaysForDaysOfFoodAndMeals(2, ['breakfast', 'lunch'])
   *
   * Will return:
   * [
   * 	{
   * 		"day": "Saturday",
   * 		"meals" [
   * 			"breakfast", "lunch"
   * 		]
   * 	},
   * 	{
   * 		"day": "Sunday",
   * 		"meals" [
   * 			"breakfast", "lunch"
   * 		]
   * 	}
   * ]
   */
  getMealDaysForDaysOfFoodAndMeals(
    daysOfFood: number,
    meals: string[],
  ): MealsForMealDay[] {
    const deliveries = this.getDeliveriesForDaysOfFoodAndMeals(
      daysOfFood,
      meals,
    )
    const mealCodes = deliveries.map((delivery) => delivery.mealCodes).flat()

    return mealCodes.reduce((mealDays, mealCode) => {
      const day = Meal.getDateFromMealCode(mealCode).format('dddd')
      const mealType = Meal.getMealTypeFromMealCode(mealCode)

      const previousMealDay = mealDays[mealDays.length - 1]

      if (previousMealDay && previousMealDay.day === day) {
        previousMealDay.meals.push(mealType)
      } else {
        mealDays.push({
          day,
          meals: [mealType],
        })
      }

      return mealDays
    }, [] as MealsForMealDay[])
  }

  /**
   * If there is an exception defined for the given week, return it.
   * Otherwise, return the regular delivery zone.
   *
   * @param {dayjs} week
   */
  getDeliveryZoneForWeek(week: Dayjs) {
    const deliveryZoneException = this.getDeliveryZoneException(week)

    return deliveryZoneException || this
  }
}

export class DeliveryZoneException extends DeliveryZone {
  exceptionName: any
  week: any

  constructor(rawDeliveryZoneException: PopsDeliveryZoneException) {
    super(rawDeliveryZoneException as any)

    this.exceptionName = rawDeliveryZoneException.exception_name
    this.totalDeliverableMeals =
      rawDeliveryZoneException.total_deliverable_meals || 0
    this.week = dayjs(rawDeliveryZoneException.week)
  }
}
